/*
 * Registertabelle eines Geräts (docs/servicezugang-oem-und-fern.md, Teil 4).
 *
 * Der volle Adressraum für den Servicezugang, am Panel und aus der Ferne,
 * ohne das Betriebsschema anzufassen. Gebraucht vor allem bei Modbus: dort
 * gibt es keine Erkundung, die Tabelle des Herstellers ist das OEM-Schema.
 * Das Gerät holt sie stündlich und nach dem Start.
 *
 * Ablauf: Datei wählen (CSV oder JSON), der Server prüft sie ohne zu
 * speichern, man sieht Anzahl und Hinweise, erst "Übernehmen" ersetzt die
 * Tabelle. Ebene und Schreibbarkeit lassen sich je Zeile ändern; zum
 * Bearbeiten vieler Zeilen gibt es die CSV für Excel, die der Import wieder
 * liest. Lesen mit plants.read, ändern mit plants.admin.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { sessionSignal } from '../auth/session';
import { showToast } from './Toaster';
import {
  countByLevel, filterRows, LEVEL_NAMES, LEVELS, toCsv, toRow, withHint,
  type Level, type RawAddress,
} from './registerMapModel';
import './hv-modal.css';
import './RegisterMapModal.css';

interface Props {
  deviceId: string;
  deviceName: string;
  protocol: string | null;
  onClose(): void;
}

interface MapDto {
  deviceId: string;
  count: number;
  changedAt: string;
  changedBy: string | null;
  sourceName: string | null;
  addresses: RawAddress[] | null;
}

interface Preview {
  fileName: string;
  content: string;
  count: number;
  warnings: string[];
}

/** So viele Zeilen zeigt die Tabelle; mehr findet man über den Filter. */
const SHOWN = 300;

const errorText = (e: unknown) =>
  e instanceof ApiError
    ? ((e.body as { error?: string } | null)?.error ?? (e.status === 403 ? 'Dafür fehlt die Berechtigung.' : `HTTP ${e.status}`))
    : e instanceof Error ? e.message : String(e);

export function RegisterMapModal({ deviceId, deviceName, protocol, onClose }: Props) {
  const base = `/api/devices/${deviceId}/register-map`;
  const canEdit = sessionSignal.value.user?.permissions.includes('plants.admin') ?? false;
  const [map, setMap] = useState<MapDto | null>(null);
  const [addresses, setAddresses] = useState<RawAddress[]>([]);
  const [dirty, setDirty] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<Level | ''>('');
  const [preview, setPreview] = useState<Preview | null>(null);

  const load = () => {
    setLoading(true);
    api.get<MapDto>(base)
      .then((m) => { setMap(m); setAddresses(m.addresses ?? []); setDirty(0); setError(null); })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) { setMap(null); setAddresses([]); setDirty(0); setError(null); return; }
        setError(errorText(e));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [deviceId]);

  const rows = useMemo(() => addresses.map(toRow), [addresses]);
  const shown = useMemo(() => filterRows(rows, query, level), [rows, query, level]);
  const counts = useMemo(() => countByLevel(rows), [rows]);

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const content = await file.text();
      const r = await api.post<{ count: number; warnings: string[] }>(`${base}/import`,
        { content, sourceName: file.name, dryRun: true });
      setPreview({ fileName: file.name, content, count: r.count, warnings: r.warnings ?? [] });
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { warnings?: string[] } | null) : null;
      showToast({ tone: 'error', title: 'Datei nicht lesbar', message: [errorText(e), ...(body?.warnings ?? []).slice(0, 3)].join(' ') });
    } finally {
      setBusy(false);
    }
  };

  const applyPreview = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.post<{ count: number }>(`${base}/import`,
        { content: preview.content, sourceName: preview.fileName, dryRun: false });
      showToast({ tone: 'success', title: 'Registertabelle übernommen', message: `${r.count} Adressen. Das Gerät holt sie binnen einer Stunde.` });
      setPreview(null);
      load();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht übernommen', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(base, { addresses, sourceName: map?.sourceName ?? null });
      showToast({ tone: 'success', title: 'Gespeichert', message: 'Das Gerät holt die Änderung binnen einer Stunde.' });
      load();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gespeichert', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Registertabelle löschen? Der Servicezugang zeigt dann nur noch, was das Gerät selbst erkundet.')) return;
    setBusy(true);
    try {
      await api.delete(base);
      load();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gelöscht', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const blob = new Blob([toCsv(addresses)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registertabelle-${(deviceName || deviceId).replace(/[^\p{L}\p{N}._-]+/gu, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hint = (address: string, key: 'oem.level' | 'oem.writable', value: string) => {
    setAddresses((prev) => withHint(prev, address, key, value));
    setDirty((d) => d + 1);
  };

  const close = () => {
    if (busy) return;
    if (dirty > 0 && !window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
    onClose();
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={close}>
      <div class="hv-modal hv-modal--wide hv-regmap" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Registertabelle · {deviceName}</h2>
          <button type="button" class="hv-modal__close" onClick={close} aria-label="Schließen" disabled={busy}>×</button>
        </div>
        <div class="hv-modal__body">
          <p class="hv-modal__hint">
            Der volle Adressraum dieses Geräts für den Servicezugang, am Panel und aus der Ferne. Das Betriebsschema
            bleibt davon unberührt. Punkte ohne Ebene sind nur mit Servicestick oder dem Recht OEM sichtbar.
            {protocol?.toLowerCase().includes('modbus') && ' Bei Modbus braucht jede Zeile Adresse (h:, i:, c:, d:) und modbus.type.'}
          </p>
          {error && <p class="hv-modal__error">{error}</p>}
          {loading && <p class="muted">Lade…</p>}

          {!loading && !error && (
            <div class="hv-regmap__status">
              {map
                ? <span>{map.count} Adressen, Stand {new Date(map.changedAt).toLocaleString('de-AT')}{map.changedBy ? ` von ${map.changedBy}` : ''}{map.sourceName ? `, aus ${map.sourceName}` : ''}.</span>
                : <span>Keine Registertabelle hinterlegt. Der Servicezugang zeigt nur, was das Gerät selbst erkundet.</span>}
              {rows.length > 0 && (
                <span class="hv-regmap__levels">
                  {LEVELS.map((l) => <span key={l}>{LEVEL_NAMES[l]} {counts[l]}</span>)}
                </span>
              )}
            </div>
          )}

          {preview && (
            <section class="hv-regmap__preview">
              <h3>Geprüft: {preview.fileName}</h3>
              <p>
                {preview.count} Adressen gelesen.
                {map ? ` Ersetzt die hinterlegte Tabelle mit ${map.count} Adressen vollständig.` : ''}
                {' '}Noch nichts gespeichert.
              </p>
              {preview.warnings.length > 0 && (
                <ul class="hv-regmap__warnings">{preview.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
              )}
              <div class="hv-regmap__actions">
                <button type="button" class="hv-btn" disabled={busy} onClick={() => setPreview(null)}>Verwerfen</button>
                <button type="button" class="hv-btn hv-btn--primary" disabled={busy} onClick={() => void applyPreview()}>
                  {busy ? 'Übernehme…' : 'Übernehmen'}
                </button>
              </div>
            </section>
          )}

          {!loading && !error && (
            <div class="hv-regmap__toolbar">
              <input type="search" placeholder="Adresse, Nummer, Bezeichnung oder Gruppe" value={query}
                     onInput={(e) => setQuery((e.target as HTMLInputElement).value)} disabled={rows.length === 0} />
              <select value={level} onChange={(e) => setLevel((e.target as HTMLSelectElement).value as Level | '')} disabled={rows.length === 0}>
                <option value="">Alle Ebenen</option>
                {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_NAMES[l]}</option>)}
              </select>
              {canEdit && (
                <label class={`hv-btn hv-btn--sm${busy ? ' is-disabled' : ''}`}>
                  Datei importieren…
                  <input type="file" accept=".csv,.json,.txt,text/csv,application/json" hidden disabled={busy}
                         onChange={(e) => { const input = e.target as HTMLInputElement; void pickFile(input.files?.[0]); input.value = ''; }} />
                </label>
              )}
              <button type="button" class="hv-btn hv-btn--sm" disabled={rows.length === 0} onClick={download}>Als CSV herunterladen</button>
            </div>
          )}

          {rows.length > 0 && (
            <div class="hv-regmap__table-wrap">
              <table class="hv-regmap__table">
                <thead>
                  <tr>
                    <th>Adresse</th><th>Nr.</th><th>Bezeichnung</th><th>Gruppe</th><th>Einheit</th><th>Bereich</th>
                    <th>Ebene</th><th>schreibbar</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.slice(0, SHOWN).map((r) => (
                    <tr key={r.address}>
                      <td class="hv-regmap__mono">{r.address}</td>
                      <td>{r.line}</td>
                      <td>{r.label}</td>
                      <td>{r.group}</td>
                      <td>{r.unit}</td>
                      <td>{r.min || r.max ? `${r.min.replace('.', ',')} bis ${r.max.replace('.', ',')}` : ''}</td>
                      <td>
                        {canEdit
                          ? (
                            <select value={r.level} disabled={busy}
                                    onChange={(e) => hint(r.address, 'oem.level', (e.target as HTMLSelectElement).value)}>
                              {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_NAMES[l]}</option>)}
                            </select>
                          )
                          : LEVEL_NAMES[r.level]}
                      </td>
                      <td>
                        <input type="checkbox" checked={r.writable} disabled={!canEdit || busy}
                               aria-label={`${r.label} schreibbar`}
                               onChange={(e) => hint(r.address, 'oem.writable', String((e.target as HTMLInputElement).checked))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p class="muted">
                {shown.length > SHOWN
                  ? `${shown.length} Treffer, die ersten ${SHOWN} stehen da. Mit dem Filter weiter eingrenzen.`
                  : `${shown.length} von ${rows.length} Adressen.`}
              </p>
            </div>
          )}
        </div>
        <div class="hv-modal__footer">
          {canEdit && map && (
            <button type="button" class="hv-btn hv-btn--danger-outline" disabled={busy} onClick={() => void remove()}>Löschen</button>
          )}
          <span class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn" disabled={busy} onClick={close}>Schließen</button>
          {canEdit && (
            <button type="button" class="hv-btn hv-btn--primary" disabled={busy || dirty === 0} onClick={() => void save()}>
              {dirty > 0 ? `Änderungen speichern (${dirty})` : 'Keine Änderungen'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
